import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authService } from "../../services";
import { BUSINESS_MODELS } from "../../config/business-models";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Button, Input, PhoneInput, Select } from "../../components/ui";
import Icon from "../../components/ui/Icon";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// หน้านี้เป็นการสมัคร "ฝั่งผู้ดูแลร้าน" เท่านั้น — 1 บัญชี = 1 ร้าน
// ลูกค้าสมัครที่ /auth/register/customer ผ่านลิงก์ของร้านเท่านั้น
export default function Register() {
  const navigate = useNavigate();
  const { toast } = useApp();
  const { t, tx, pick } = useI18n();

  const [form, setForm] = useState({
    shopName: "",
    businessModel: BUSINESS_MODELS[0].key,
    fullname: "",
    phone: "",
    email: "",
    address: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const onChange = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const validate = () => {
    if (!form.shopName) return tx("กรุณากรอกชื่อร้าน/ธุรกิจ", "Please enter your shop name");
    if (!form.fullname) return tx("กรุณากรอกชื่อ-นามสกุล", "Please enter your name");
    if (form.phone.length !== 10) return tx("เบอร์โทรศัพท์ต้องเป็นตัวเลข 10 หลัก", "Phone must be 10 digits");
    if (!EMAIL_PATTERN.test(form.email)) return tx("รูปแบบอีเมลไม่ถูกต้อง", "Invalid email format");
    if (!form.address) return tx("กรุณากรอกที่อยู่", "Please enter address");
    if (form.password.length < 6) return tx("รหัสผ่านต้องมี 6 ตัวอักษรขึ้นไป", "Password must be at least 6 characters");
    if (form.password !== form.confirmPassword) return tx("รหัสผ่านไม่ตรงกัน", "Passwords do not match");
    return "";
  };

  const submit = async (event) => {
    event.preventDefault();
    const message = validate();
    setError(message);
    if (message) return;

    setLoading(true);
    try {
      const created = await authService.registerAdmin(form);
      setDone(created);
      toast(tx("เปิดร้านสำเร็จ", "Shop created"), "success");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast(tx("คัดลอกแล้ว", "Copied"), "success");
    } catch (e) {
      toast(tx("คัดลอกไม่สำเร็จ", "Copy failed"), "error");
    }
  };

  // สมัครเสร็จ -> โชว์รหัสร้านและลิงก์สำหรับส่งให้ลูกค้าสมัคร
  if (done) {
    return (
      <>
        <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 6 }}>{tx("เปิดร้านเรียบร้อย", "Your shop is ready")}</h1>
        <p className="u-muted u-mb-16">
          {tx("เก็บลิงก์นี้ไว้ส่งให้ลูกค้าสมัครสมาชิกกับร้านของคุณ", "Share this link so your customers can sign up with your shop")}
        </p>

        <div className="card-crm u-mb-16">
          <div className="u-fs-13 u-muted">{tx("รหัสร้าน", "Shop code")}</div>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: 2 }}>{done.code}</div>

          <div className="u-fs-13 u-muted u-mt-16">{tx("ลิงก์สมัครของลูกค้า", "Customer sign-up link")}</div>
          <div className="u-fs-13" style={{ wordBreak: "break-all" }}>{done.registerLink}</div>

          <Button className="u-mt-8" size="sm" onClick={() => copy(done.registerLink)}>
            <Icon name="copy" /> {tx("คัดลอกลิงก์", "Copy link")}
          </Button>
        </div>

        <div className="u-fs-13 u-muted u-mb-16">
          {tx(
            "ลูกค้าที่สมัครผ่านลิงก์นี้จะอยู่ในร้านของคุณเท่านั้น ร้านอื่นมองไม่เห็น",
            "Customers who sign up via this link belong to your shop only."
          )}
        </div>

        <Button variant="primary" block onClick={() => navigate("/auth/login")}>
          {t("action.login")}
        </Button>
      </>
    );
  }

  return (
    <>
      <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 6 }}>{tx("เปิดร้านใหม่ (ผู้ดูแล)", "Open a shop (admin)")}</h1>
      <p className="u-muted u-mb-16">
        {tx("สมัครเพื่อเป็นผู้ดูแลร้านของคุณเอง 1 บัญชี = 1 ร้าน", "Sign up as the owner of your own shop — one account, one shop")}
      </p>

      <form onSubmit={submit}>
        <Input label={tx("ชื่อร้าน / ชื่อธุรกิจ", "Shop name")} value={form.shopName} onChange={onChange("shopName")} />
        <Select
          label={tx("ประเภทธุรกิจ", "Business model")}
          options={BUSINESS_MODELS.map((model) => ({ value: model.key, label: pick(model) }))}
          value={form.businessModel}
          onChange={onChange("businessModel")}
        />

        <Input label={tx("ชื่อ-นามสกุลผู้ดูแล", "Owner full name")} value={form.fullname} onChange={onChange("fullname")} />
        <PhoneInput label={t("common.phone")} value={form.phone} onChange={(value) => setForm((prev) => ({ ...prev, phone: value }))} />
        <Input label={t("common.email")} type="email" value={form.email} onChange={onChange("email")} />
        <Input label={tx("ที่อยู่ / ที่ตั้งธุรกิจ", "Address")} value={form.address} onChange={onChange("address")} />
        <Input label={t("auth.password")} type="password" value={form.password} onChange={onChange("password")} autoComplete="new-password" />
        <Input label={t("auth.confirmPassword")} type="password" value={form.confirmPassword} onChange={onChange("confirmPassword")} autoComplete="new-password" />

        {error ? <div className="field-error u-mb-8">{error}</div> : null}

        <Button type="submit" variant="primary" block disabled={loading}>
          {loading ? tx("กำลังเปิดร้าน...", "Creating shop...") : tx("เปิดร้าน", "Create shop")}
        </Button>
      </form>

      <div className="u-fs-13 u-muted u-mt-16">
        {tx(
          "เป็นลูกค้าของร้านใช่ไหม? ต้องสมัครผ่านลิงก์ที่ร้านส่งให้เท่านั้น",
          "Are you a customer? You can only sign up through the link your shop sends you."
        )}
      </div>

      <div className="u-center u-mt-16 u-fs-13">
        {t("auth.hasAccount")} <Link to="/auth/login">{t("action.login")}</Link>
      </div>
    </>
  );
}
